import { ConfigurationProvider } from "../configuration/ConfigurationProvider";

/**
 * The attribute that carries an attachment URL instead of `src`. The markdown renderer (`pageFileMarkdown`) and the
 * components that show attachments write it, so the browser never requests the file without a token; `PageFileResolver`
 * fetches it with the bearer and sets `src` to an object URL.
 */
export const PAGE_FILE_SRC_ATTRIBUTE = "data-ark-src";

/**
 * Ark attachments are served by Ark.Core under `/api/Spaces/{s}/Pages/{p}/PageFiles/{key}`, and page bodies carry those
 * URLs absolute (`https://apis.ark…/api/Spaces/48/Pages/1203/PageFiles/<guid>/diagram.png`). Since OrbitalPlan/18 phase 0 the
 * route needs a bearer token, so a plain `<img src>` cannot load it: the SPA fetches these URLs itself (see
 * `PageFileResolver`). Only URLs on the configured backend origin qualify, so the token never goes to another host.
 */
const PAGE_FILE_PATH = /^\/api\/spaces\/\d+\/pages\/\d+\/pagefiles\/.+/i;

/** The origin of `App.BackendUrl`, or null when the configuration is not loaded or not an absolute URL. */
export function backendOrigin(): string | null {
    try {
        const backendUrl = ConfigurationProvider.getConfiguration()?.App?.BackendUrl;
        return backendUrl ? new URL(backendUrl).origin : null;
    } catch {
        return null;
    }
}

/** Is `url` an Ark attachment on the backend origin (absolute, or relative to the current page)? */
export function isPageFileUrl(url: string | null | undefined, origin: string | null = backendOrigin()): boolean {
    if (!url || !origin) return false;
    let parsed: URL;
    try {
        parsed = new URL(url, window.location.href);
    } catch {
        return false;
    }
    return parsed.origin === origin && PAGE_FILE_PATH.test(parsed.pathname);
}

/** The file name at the end of an attachment URL (`…/PageFiles/<guid>/diagram.png` → `diagram.png`). */
export function pageFileName(url: string): string {
    try {
        const path = new URL(url, window.location.href).pathname;
        return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "download";
    } catch {
        return "download";
    }
}

/** Opened in a browser tab when clicked; anything else is downloaded under its own name. */
export function isViewableInBrowser(url: string): boolean {
    return /\.(png|jpe?g|gif|bmp|webp|svg|pdf|txt)$/i.test(pageFileName(url));
}
